import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { resolveCrmProfileUsername, resolveCrmProfileActivationId } from '@/shared/auth/auth.reducer';
import { mapCaseTransferToListRow, } from '../services/caseTransfer.mapper';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
import { appendCriteriaEquals, appendCriteriaIn, appendPageable, readPagedRows } from '../utils/spring-api';
import { buildCaseTransferWorkflowRequestBody, resolveCaseTransferAssignmentId, resolveCaseTransferFromDepartmentId, resolveCaseTransferOriginMode, resolveCaseTransferTargetUsername, } from '../services/caseTransferRequest.service';
import { mapCrmDepartmentReference, resolveUserDepartmentSupervisorUsername } from '../utils/supervisor-options';
const base = API_ROUTES.caseTransfers;
const workflow = API_ROUTES.caseTransferWorkflow;
const MY_TRANSFER_HISTORY_STATUSES = ['APPROVED', 'REJECTED', 'COMPLETED'];
function myTransferHistoryQuery(page, size, sort, username) {
    const q = new URLSearchParams();
    appendPageable(q, page, size, sort);
    appendCriteriaEquals(q, 'fromUserUsername', username);
    appendCriteriaIn(q, 'status', MY_TRANSFER_HISTORY_STATUSES);
    return q.toString();
}
function myPendingOutboundQuery(page, size, sort, username) {
    const q = new URLSearchParams();
    appendPageable(q, page, size, sort);
    appendCriteriaEquals(q, 'fromUserUsername', username);
    appendCriteriaIn(q, 'status', ['PENDING']);
    return q.toString();
}
function pendingApprovalsQuery(page, size, sort) {
    const q = new URLSearchParams();
    appendPageable(q, page, size, sort);
    appendCriteriaEquals(q, 'requiresApproval', true);
    appendCriteriaIn(q, 'status', ['PENDING']);
    return q.toString();
}
function pendingApprovalsCountQuery() {
    const q = new URLSearchParams();
    appendCriteriaEquals(q, 'requiresApproval', true);
    appendCriteriaIn(q, 'status', ['PENDING']);
    return q.toString();
}
function historyTransferQuery(page, size, sort, approvedByUsername, status) {
    const q = new URLSearchParams();
    appendPageable(q, page, size, sort);
    appendCriteriaEquals(q, 'approvedByUsername', approvedByUsername);
    appendCriteriaEquals(q, 'status', status);
    return q.toString();
}
async function mapTransferRows(rows) {
    const caseIds = [
        ...new Set(rows
            .filter((row) => !row.kase)
            .map((row) => String(row.kaseId ?? ''))
            .filter(Boolean)),
    ];
    const caseMap = await fetchCasesByIds(caseIds);
    return rows.map((row) => mapCaseTransferToListRow(row, row.kase ?? caseMap.get(String(row.kaseId ?? ''))));
}
async function fetchCasesByIds(caseIds) {
    if (!caseIds.length) {
        return new Map();
    }
    const q = new URLSearchParams();
    appendPageable(q, 0, caseIds.length, 'id,desc');
    appendCriteriaIn(q, 'id', caseIds);
    const res = await axios.get(`${API_ROUTES.kases}?${q.toString()}`);
    const { rows } = readPagedRows(res.data, res.headers);
    return new Map(rows.map((row) => [String(row.id ?? ''), row]));
}
const initialState = {
    errorMessage: null,
    reference: null,
    referenceLoading: false,
    myCases: [],
    myCasesTotal: 0,
    myCasesLoading: false,
    pendingTransfers: [],
    pendingTotal: 0,
    pendingLoading: false,
    pendingApprovalsCount: 0,
    pendingCountLoading: false,
    myTransfers: [],
    myTransfersTotal: 0,
    myTransfersLoading: false,
    historyTransfers: [],
    historyTotal: 0,
    historyLoading: false,
    actionLoading: false,
};
export const getCaseTransferReference = createAsyncThunk(`${API_ROUTES.caseTransfers}/reference`, async () => {
    const [usersRes, deptRes] = await Promise.all([
        axios.get(`${API_ROUTES.users}?page=0&size=1000&sort=id,desc`),
        axios.get(`${API_ROUTES.crmDepartments}?page=0&size=1000&sort=id,desc`),
    ]);
    const users = Array.isArray(usersRes.data) ? usersRes.data : usersRes.data?.content ?? [];
    const departments = Array.isArray(deptRes.data) ? deptRes.data : deptRes.data?.content ?? [];
    return {
        data: {
            users: users.map((u) => ({
                id: String(u.id ?? u.login ?? u.username ?? ''),
                name: u.fullName ?? u.login ?? u.username ?? '',
                username: u.login ?? u.username,
            })),
            departments: departments.map((d) => mapCrmDepartmentReference(d)),
            reasons: [
                { value: 'WORKLOAD_BALANCE', label: 'Workload balance' },
                { value: 'EXPERTISE_REQUIRED', label: 'Expertise required' },
                { value: 'ESCALATION', label: 'Escalation' },
                { value: 'AGENT_REQUEST', label: 'Agent request' },
                { value: 'SUPERVISOR_DECISION', label: 'Supervisor decision' },
                { value: 'DEPARTMENT_CHANGE', label: 'Department change' },
                { value: 'UNAVAILABLE', label: 'Unavailable' },
                { value: 'CONFLICT', label: 'Conflict' },
                { value: 'CUSTOMER_REQUEST', label: 'Customer request' },
            ],
        },
    };
}, { serializeError: serializeAxiosError });
export const getCaseTransferMyCases = createAsyncThunk(`${API_ROUTES.kases}/my-cases`, async ({ page, size, sort }, { getState }) => {
    const auth = getState().auth;
    const username = resolveCrmProfileUsername(auth.crmProfile) ?? auth.session?.username;
    if (!username) {
        return { data: [], headers: { 'x-total-count': '0' } };
    }
    const q = new URLSearchParams();
    appendPageable(q, page, size, sort);
    appendCriteriaEquals(q, 'followUp', true);
    appendCriteriaEquals(q, 'assignedAgentUsername', username);
    const res = await axios.get(`${API_ROUTES.kases}?${q.toString()}`);
    const { rows, total } = readPagedRows(res.data, res.headers);
    return { data: rows, headers: { 'x-total-count': String(total) } };
}, { serializeError: serializeAxiosError });
export const getCaseTransferPendingCount = createAsyncThunk(`${base}/pending-count`, async (_, { getState }) => {
    const username = resolveCrmProfileUsername(getState().auth.crmProfile);
    if (!username) {
        return { data: { count: 0 } };
    }
    const res = await axios.get(`${base}/count?${pendingApprovalsCountQuery()}`);
    return { data: { count: typeof res.data === 'number' ? res.data : 0 } };
}, { serializeError: serializeAxiosError });
export const getCaseTransferPending = createAsyncThunk(`${base}/pending`, async ({ page, size, sort }, { getState }) => {
    const username = resolveCrmProfileUsername(getState().auth.crmProfile);
    if (!username) {
        return { data: [], headers: { 'x-total-count': '0' } };
    }
    const res = await axios.get(`${base}?${pendingApprovalsQuery(page, size, sort)}`);
    const { rows, total } = readPagedRows(res.data, res.headers);
    const mappedRows = await mapTransferRows(rows);
    return { data: mappedRows, headers: { 'x-total-count': String(total) } };
}, { serializeError: serializeAxiosError });
export const getCaseTransferMyPendingOutbound = createAsyncThunk(`${base}/my-pending-outbound`, async (_, { getState }) => {
    const username = resolveCrmProfileUsername(getState().auth.crmProfile);
    if (!username) {
        return { data: [] };
    }
    const res = await axios.get(`${base}?${myPendingOutboundQuery(0, 1000, 'id,desc', username)}`);
    const { rows } = readPagedRows(res.data, res.headers);
    const mappedRows = await mapTransferRows(rows);
    return { data: mappedRows };
}, { serializeError: serializeAxiosError });
export const getCaseTransferMyTransfers = createAsyncThunk(`${base}/my-transfers`, async ({ page, size, sort }, { getState }) => {
    const username = resolveCrmProfileUsername(getState().auth.crmProfile);
    if (!username) {
        return { data: [], headers: { 'x-total-count': '0' } };
    }
    const res = await axios.get(`${base}?${myTransferHistoryQuery(page, size, sort, username)}`);
    const { rows, total } = readPagedRows(res.data, res.headers);
    const mappedRows = await mapTransferRows(rows);
    return { data: mappedRows, headers: { 'x-total-count': String(total) } };
}, { serializeError: serializeAxiosError });
export const getCaseTransferHistory = createAsyncThunk(`${base}/history`, async ({ page, size, sort, historyStatus }, { getState }) => {
    const username = resolveCrmProfileUsername(getState().auth.crmProfile);
    if (!username) {
        return { data: [], headers: { 'x-total-count': '0' } };
    }
    const status = historyStatus && historyStatus !== 'ALL' ? historyStatus : undefined;
    const res = await axios.get(`${base}?${historyTransferQuery(page, size, sort, username, status)}`);
    const { rows, total } = readPagedRows(res.data, res.headers);
    const mappedRows = await mapTransferRows(rows);
    return { data: mappedRows, headers: { 'x-total-count': String(total) } };
}, { serializeError: serializeAxiosError });
async function fetchActiveCaseAssignments(caseId) {
    const q = new URLSearchParams();
    appendPageable(q, 0, 1000);
    appendCriteriaEquals(q, 'kaseId', String(caseId));
    appendCriteriaEquals(q, 'active', true);
    const res = await axios.get(`${API_ROUTES.caseAssignments}?${q.toString()}`);
    const { rows } = readPagedRows(res.data, res.headers);
    return rows;
}
export const submitCaseTransferRequest = createAsyncThunk(`${workflow.request('')}`, async (payload, { getState }) => {
    const state = getState();
    const fromUsername = resolveCrmProfileUsername(state.auth.crmProfile) ?? state.auth.session?.username;
    const currentActivationId = resolveCrmProfileActivationId(state.auth.crmProfile);
    const originMode = resolveCaseTransferOriginMode(payload.selectedAgentIds, currentActivationId);
    const fromDepartmentId = resolveCaseTransferFromDepartmentId(payload.assignedDepartmentId);
    const users = state.caseTransferPage.reference?.users ?? [];
    const departments = state.caseTransferPage.reference?.departments ?? [];
    const targetUserId = payload.targetUserIds?.[0];
    const targetDepartmentId = payload.targetDepartmentIds?.[0];
    const toUsername = payload.transferToUser
        ? resolveCaseTransferTargetUsername(targetUserId, users)
        : undefined;
    const toDepartmentId = !payload.transferToUser && targetDepartmentId != null && targetDepartmentId !== ''
        ? Number(targetDepartmentId)
        : undefined;
    let caseAssignmentId = payload.assignmentId ? Number(payload.assignmentId) : undefined;
    if (caseAssignmentId == null || !Number.isFinite(caseAssignmentId)) {
        const assignments = await fetchActiveCaseAssignments(payload.caseId);
        caseAssignmentId = resolveCaseTransferAssignmentId(assignments, originMode, fromUsername, fromDepartmentId);
    }
    const approvedByUsername = payload.requiresApproval
        ? payload.approvedByUsername ??
            (fromUsername ? resolveUserDepartmentSupervisorUsername(fromUsername, departments) : undefined)
        : undefined;
    const body = buildCaseTransferWorkflowRequestBody({
        originMode,
        transferToUser: payload.transferToUser,
        reason: payload.reason,
        transferNotes: payload.transferNotes,
        requiresApproval: payload.requiresApproval ?? false,
        fromUsername: originMode === 'agent' ? fromUsername : undefined,
        fromDepartmentId: originMode === 'department' ? fromDepartmentId : undefined,
        toUsername,
        toDepartmentId: Number.isFinite(toDepartmentId) ? toDepartmentId : undefined,
        caseAssignmentId: Number.isFinite(caseAssignmentId) ? caseAssignmentId : undefined,
        approvedByUsername,
    });
    return axios.post(workflow.request(payload.caseId), body);
}, { serializeError: serializeAxiosError });
export const approveCaseTransfer = createAsyncThunk(`${workflow.approve('')}`, async ({ transferId, approvalNotes }) => axios.post(workflow.approve(transferId), { notes: approvalNotes ?? '' }), { serializeError: serializeAxiosError });
export const rejectCaseTransfer = createAsyncThunk(`${workflow.reject('')}`, async ({ transferId, approvalNotes }) => axios.post(workflow.reject(transferId), { notes: approvalNotes }), { serializeError: serializeAxiosError });
export const cancelCaseTransfer = createAsyncThunk(`${workflow.cancel('')}`, async (payload) => axios.post(workflow.cancel(payload.transferId), { notes: payload.notes ?? '' }), { serializeError: serializeAxiosError });
export const caseTransferPageSlice = createSlice({
    name: 'caseTransferPage',
    initialState: initialState,
    reducers: {
        clearCaseTransferError(state) {
            state.errorMessage = null;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getCaseTransferReference.fulfilled, (state, action) => {
            state.referenceLoading = false;
            state.reference = action.payload.data;
        })
            .addCase(getCaseTransferMyCases.fulfilled, (state, action) => {
            state.myCasesLoading = false;
            state.myCases = action.payload.data;
            state.myCasesTotal = parseInt(action.payload.headers['x-total-count'] || '0', 10);
        })
            .addCase(getCaseTransferPendingCount.fulfilled, (state, action) => {
            state.pendingCountLoading = false;
            state.pendingApprovalsCount = action.payload.data.count ?? 0;
        })
            .addCase(getCaseTransferPending.fulfilled, (state, action) => {
            state.pendingLoading = false;
            state.pendingTransfers = action.payload.data;
            state.pendingTotal = parseInt(action.payload.headers['x-total-count'] || '0', 10);
        })
            .addCase(getCaseTransferMyTransfers.fulfilled, (state, action) => {
            state.myTransfersLoading = false;
            state.myTransfers = action.payload.data;
            state.myTransfersTotal = parseInt(action.payload.headers['x-total-count'] || '0', 10);
        })
            .addCase(getCaseTransferHistory.fulfilled, (state, action) => {
            state.historyLoading = false;
            state.historyTransfers = action.payload.data;
            state.historyTotal = parseInt(action.payload.headers['x-total-count'] || '0', 10);
        })
            .addMatcher(isPending(getCaseTransferReference), (state) => {
            state.referenceLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getCaseTransferMyCases), (state) => {
            state.myCasesLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getCaseTransferPendingCount), (state) => {
            state.pendingCountLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getCaseTransferPending), (state) => {
            state.pendingLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getCaseTransferMyTransfers), (state) => {
            state.myTransfersLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(getCaseTransferHistory), (state) => {
            state.historyLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isPending(submitCaseTransferRequest, approveCaseTransfer, rejectCaseTransfer, cancelCaseTransfer), (state) => {
            state.actionLoading = true;
            state.errorMessage = null;
        })
            .addMatcher(isFulfilled(submitCaseTransferRequest, approveCaseTransfer, rejectCaseTransfer, cancelCaseTransfer), (state) => {
            state.actionLoading = false;
        })
            .addMatcher(isRejected(getCaseTransferReference), (state, action) => {
            state.referenceLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(getCaseTransferMyCases), (state, action) => {
            state.myCasesLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(getCaseTransferPendingCount), (state, action) => {
            state.pendingCountLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(getCaseTransferPending), (state, action) => {
            state.pendingLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(getCaseTransferMyTransfers), (state, action) => {
            state.myTransfersLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(getCaseTransferHistory), (state, action) => {
            state.historyLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(submitCaseTransferRequest, approveCaseTransfer, rejectCaseTransfer, cancelCaseTransfer), (state, action) => {
            state.actionLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { clearCaseTransferError } = caseTransferPageSlice.actions;
export default caseTransferPageSlice.reducer;
